/**
 * countryCodes.js
 *
 * All ITU-assigned country calling codes worth showing in a mobile-number
 * picker: every UN member state, plus a handful of commonly-listed
 * non-UN entities/territories (Taiwan, Hong Kong, Macao, Kosovo,
 * Palestine, Vatican City, Puerto Rico) and — because NANP nations all
 * share "+1" — each Caribbean NANP member broken out individually by its
 * own area code (e.g. Jamaica "+1876", not lumped under a single "+1").
 *
 * Digit-length validation:
 *  - PRECISE_RULES holds country-verified `min`/`max` national-number
 *    digit counts (and, where relevant, a `leading` pattern) for ~55
 *    high-traffic countries — the original curated set plus a few more
 *    (China, Japan, Brazil, Russia, Nigeria, Indonesia, etc.) confident
 *    enough to state exactly.
 *  - Every other country falls back to a GENERIC range derived from the
 *    real E.164 constraint (total number, country code included, never
 *    exceeds 15 digits) rather than a guessed exact length. This is
 *    intentionally lenient, not precise — it won't reject a real user's
 *    valid number, but it also won't catch every malformed one. For
 *    country-exact validation everywhere, wire in a library like
 *    libphonenumber-js instead of hand-maintaining this table further.
 *
 * Flags are generated from the ISO 3166-1 alpha-2 code (regional
 * indicator symbols) rather than hardcoded per country — one formula,
 * correct for all ~200 entries, instead of 200 chances to typo an emoji.
 */

const isoToFlagEmoji = (iso2) =>
  iso2
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const GENERIC_MIN_DIGITS = 4;

/**
 * `min`/`max` = digits in the *national* number (after the dial code, after
 * a leading 0 is stripped). `leading` = optional pattern the first digit(s)
 * must match. Caribbean NANP entries below use exactly 7 — the area code is
 * already folded into `dial` (e.g. Jamaica's dial is "+1876", not "+1"), so
 * the remaining national part a user types is always the 7-digit local
 * subscriber number, same as it is for a US/Canada number after its area
 * code.
 */
const PRECISE_RULES = {
  IN: { min: 10, max: 10, leading: /^[6-9]/, leadingMessage: "Indian mobile numbers start with 6, 7, 8, or 9." },
  US: { min: 10, max: 10 },
  CA: { min: 10, max: 10 },
  GB: { min: 10, max: 10 },
  AE: { min: 9, max: 9 },
  SA: { min: 9, max: 9 },
  SG: { min: 8, max: 8 },
  MY: { min: 9, max: 10 },
  AU: { min: 9, max: 9 },
  NZ: { min: 8, max: 10 },
  NP: { min: 10, max: 10 },
  LK: { min: 9, max: 9 },
  BD: { min: 10, max: 10 },
  PK: { min: 10, max: 10 },
  ZA: { min: 9, max: 9 },
  DE: { min: 10, max: 11 },
  FR: { min: 9, max: 9 },
  IT: { min: 9, max: 10 },
  ES: { min: 9, max: 9 },
  NL: { min: 9, max: 9 },
  RU: { min: 10, max: 10 },
  KZ: { min: 10, max: 10 },
  CN: { min: 11, max: 11 },
  JP: { min: 10, max: 10 },
  KR: { min: 9, max: 10 },
  BR: { min: 10, max: 11 },
  MX: { min: 10, max: 10 },
  PH: { min: 10, max: 10 },
  VN: { min: 9, max: 9 },
  ID: { min: 9, max: 12 },
  TH: { min: 9, max: 9 },
  EG: { min: 10, max: 10 },
  NG: { min: 10, max: 10 },
  KE: { min: 9, max: 9 },
  // Caribbean NANP — area code lives in `dial`, so national part is 7.
  AG: { min: 7, max: 7 },
  AI: { min: 7, max: 7 },
  BS: { min: 7, max: 7 },
  BB: { min: 7, max: 7 },
  BM: { min: 7, max: 7 },
  VG: { min: 7, max: 7 },
  KY: { min: 7, max: 7 },
  DM: { min: 7, max: 7 },
  DO: { min: 7, max: 7 },
  GD: { min: 7, max: 7 },
  JM: { min: 7, max: 7 },
  MS: { min: 7, max: 7 },
  KN: { min: 7, max: 7 },
  LC: { min: 7, max: 7 },
  VC: { min: 7, max: 7 },
  SX: { min: 7, max: 7 },
  TT: { min: 7, max: 7 },
  TC: { min: 7, max: 7 },
  VI: { min: 7, max: 7 },
  PR: { min: 7, max: 7 },
};

// { code, name, dial } — grouped by continent for readability; final export
// is sorted alphabetically by name for the picker.
const RAW_COUNTRIES = [
  // ── Africa ──
  { code: "DZ", name: "Algeria", dial: "+213" },
  { code: "AO", name: "Angola", dial: "+244" },
  { code: "BJ", name: "Benin", dial: "+229" },
  { code: "BW", name: "Botswana", dial: "+267" },
  { code: "BF", name: "Burkina Faso", dial: "+226" },
  { code: "BI", name: "Burundi", dial: "+257" },
  { code: "CV", name: "Cabo Verde", dial: "+238" },
  { code: "CM", name: "Cameroon", dial: "+237" },
  { code: "CF", name: "Central African Republic", dial: "+236" },
  { code: "TD", name: "Chad", dial: "+235" },
  { code: "KM", name: "Comoros", dial: "+269" },
  { code: "CG", name: "Congo", dial: "+242" },
  { code: "CD", name: "Congo (DRC)", dial: "+243" },
  { code: "CI", name: "Côte d'Ivoire", dial: "+225" },
  { code: "DJ", name: "Djibouti", dial: "+253" },
  { code: "EG", name: "Egypt", dial: "+20" },
  { code: "GQ", name: "Equatorial Guinea", dial: "+240" },
  { code: "ER", name: "Eritrea", dial: "+291" },
  { code: "SZ", name: "Eswatini", dial: "+268" },
  { code: "ET", name: "Ethiopia", dial: "+251" },
  { code: "GA", name: "Gabon", dial: "+241" },
  { code: "GM", name: "Gambia", dial: "+220" },
  { code: "GH", name: "Ghana", dial: "+233" },
  { code: "GN", name: "Guinea", dial: "+224" },
  { code: "GW", name: "Guinea-Bissau", dial: "+245" },
  { code: "KE", name: "Kenya", dial: "+254" },
  { code: "LS", name: "Lesotho", dial: "+266" },
  { code: "LR", name: "Liberia", dial: "+231" },
  { code: "LY", name: "Libya", dial: "+218" },
  { code: "MG", name: "Madagascar", dial: "+261" },
  { code: "MW", name: "Malawi", dial: "+265" },
  { code: "ML", name: "Mali", dial: "+223" },
  { code: "MR", name: "Mauritania", dial: "+222" },
  { code: "MU", name: "Mauritius", dial: "+230" },
  { code: "MA", name: "Morocco", dial: "+212" },
  { code: "MZ", name: "Mozambique", dial: "+258" },
  { code: "NA", name: "Namibia", dial: "+264" },
  { code: "NE", name: "Niger", dial: "+227" },
  { code: "NG", name: "Nigeria", dial: "+234" },
  { code: "RW", name: "Rwanda", dial: "+250" },
  { code: "ST", name: "Sao Tome and Principe", dial: "+239" },
  { code: "SN", name: "Senegal", dial: "+221" },
  { code: "SC", name: "Seychelles", dial: "+248" },
  { code: "SL", name: "Sierra Leone", dial: "+232" },
  { code: "SO", name: "Somalia", dial: "+252" },
  { code: "ZA", name: "South Africa", dial: "+27" },
  { code: "SS", name: "South Sudan", dial: "+211" },
  { code: "SD", name: "Sudan", dial: "+249" },
  { code: "TZ", name: "Tanzania", dial: "+255" },
  { code: "TG", name: "Togo", dial: "+228" },
  { code: "TN", name: "Tunisia", dial: "+216" },
  { code: "UG", name: "Uganda", dial: "+256" },
  { code: "ZM", name: "Zambia", dial: "+260" },
  { code: "ZW", name: "Zimbabwe", dial: "+263" },

  // ── Americas ──
  { code: "AG", name: "Antigua and Barbuda", dial: "+1268" },
  { code: "AR", name: "Argentina", dial: "+54" },
  { code: "BS", name: "Bahamas", dial: "+1242" },
  { code: "BB", name: "Barbados", dial: "+1246" },
  { code: "BZ", name: "Belize", dial: "+501" },
  { code: "BO", name: "Bolivia", dial: "+591" },
  { code: "BR", name: "Brazil", dial: "+55" },
  { code: "CA", name: "Canada", dial: "+1" },
  { code: "CL", name: "Chile", dial: "+56" },
  { code: "CO", name: "Colombia", dial: "+57" },
  { code: "CR", name: "Costa Rica", dial: "+506" },
  { code: "CU", name: "Cuba", dial: "+53" },
  { code: "DM", name: "Dominica", dial: "+1767" },
  { code: "DO", name: "Dominican Republic", dial: "+1809" },
  { code: "EC", name: "Ecuador", dial: "+593" },
  { code: "SV", name: "El Salvador", dial: "+503" },
  { code: "GD", name: "Grenada", dial: "+1473" },
  { code: "GT", name: "Guatemala", dial: "+502" },
  { code: "GY", name: "Guyana", dial: "+592" },
  { code: "HT", name: "Haiti", dial: "+509" },
  { code: "HN", name: "Honduras", dial: "+504" },
  { code: "JM", name: "Jamaica", dial: "+1876" },
  { code: "MX", name: "Mexico", dial: "+52" },
  { code: "NI", name: "Nicaragua", dial: "+505" },
  { code: "PA", name: "Panama", dial: "+507" },
  { code: "PY", name: "Paraguay", dial: "+595" },
  { code: "PE", name: "Peru", dial: "+51" },
  { code: "KN", name: "Saint Kitts and Nevis", dial: "+1869" },
  { code: "LC", name: "Saint Lucia", dial: "+1758" },
  { code: "VC", name: "Saint Vincent and the Grenadines", dial: "+1784" },
  { code: "SR", name: "Suriname", dial: "+597" },
  { code: "TT", name: "Trinidad and Tobago", dial: "+1868" },
  { code: "US", name: "United States", dial: "+1" },
  { code: "UY", name: "Uruguay", dial: "+598" },
  { code: "VE", name: "Venezuela", dial: "+58" },
  // Territories commonly listed alongside sovereign states:
  { code: "AI", name: "Anguilla", dial: "+1264" },
  { code: "BM", name: "Bermuda", dial: "+1441" },
  { code: "VG", name: "British Virgin Islands", dial: "+1284" },
  { code: "KY", name: "Cayman Islands", dial: "+1345" },
  { code: "MS", name: "Montserrat", dial: "+1664" },
  { code: "PR", name: "Puerto Rico", dial: "+1787" },
  { code: "SX", name: "Sint Maarten", dial: "+1721" },
  { code: "TC", name: "Turks and Caicos Islands", dial: "+1649" },
  { code: "VI", name: "US Virgin Islands", dial: "+1340" },

  // ── Asia ──
  { code: "AF", name: "Afghanistan", dial: "+93" },
  { code: "AM", name: "Armenia", dial: "+374" },
  { code: "AZ", name: "Azerbaijan", dial: "+994" },
  { code: "BH", name: "Bahrain", dial: "+973" },
  { code: "BD", name: "Bangladesh", dial: "+880" },
  { code: "BT", name: "Bhutan", dial: "+975" },
  { code: "BN", name: "Brunei", dial: "+673" },
  { code: "KH", name: "Cambodia", dial: "+855" },
  { code: "CN", name: "China", dial: "+86" },
  { code: "CY", name: "Cyprus", dial: "+357" },
  { code: "GE", name: "Georgia", dial: "+995" },
  { code: "IN", name: "India", dial: "+91" },
  { code: "ID", name: "Indonesia", dial: "+62" },
  { code: "IR", name: "Iran", dial: "+98" },
  { code: "IQ", name: "Iraq", dial: "+964" },
  { code: "IL", name: "Israel", dial: "+972" },
  { code: "JP", name: "Japan", dial: "+81" },
  { code: "JO", name: "Jordan", dial: "+962" },
  { code: "KZ", name: "Kazakhstan", dial: "+7" },
  { code: "KW", name: "Kuwait", dial: "+965" },
  { code: "KG", name: "Kyrgyzstan", dial: "+996" },
  { code: "LA", name: "Laos", dial: "+856" },
  { code: "LB", name: "Lebanon", dial: "+961" },
  { code: "MY", name: "Malaysia", dial: "+60" },
  { code: "MV", name: "Maldives", dial: "+960" },
  { code: "MN", name: "Mongolia", dial: "+976" },
  { code: "MM", name: "Myanmar", dial: "+95" },
  { code: "NP", name: "Nepal", dial: "+977" },
  { code: "KP", name: "North Korea", dial: "+850" },
  { code: "OM", name: "Oman", dial: "+968" },
  { code: "PK", name: "Pakistan", dial: "+92" },
  { code: "PS", name: "Palestine", dial: "+970" },
  { code: "PH", name: "Philippines", dial: "+63" },
  { code: "QA", name: "Qatar", dial: "+974" },
  { code: "SA", name: "Saudi Arabia", dial: "+966" },
  { code: "SG", name: "Singapore", dial: "+65" },
  { code: "KR", name: "South Korea", dial: "+82" },
  { code: "LK", name: "Sri Lanka", dial: "+94" },
  { code: "SY", name: "Syria", dial: "+963" },
  { code: "TW", name: "Taiwan", dial: "+886" },
  { code: "TJ", name: "Tajikistan", dial: "+992" },
  { code: "TH", name: "Thailand", dial: "+66" },
  { code: "TL", name: "Timor-Leste", dial: "+670" },
  { code: "TR", name: "Turkey", dial: "+90" },
  { code: "TM", name: "Turkmenistan", dial: "+993" },
  { code: "AE", name: "United Arab Emirates", dial: "+971" },
  { code: "UZ", name: "Uzbekistan", dial: "+998" },
  { code: "VN", name: "Vietnam", dial: "+84" },
  { code: "YE", name: "Yemen", dial: "+967" },
  // Territories/observer states commonly listed:
  { code: "HK", name: "Hong Kong", dial: "+852" },
  { code: "MO", name: "Macao", dial: "+853" },

  // ── Europe ──
  { code: "AL", name: "Albania", dial: "+355" },
  { code: "AD", name: "Andorra", dial: "+376" },
  { code: "AT", name: "Austria", dial: "+43" },
  { code: "BY", name: "Belarus", dial: "+375" },
  { code: "BE", name: "Belgium", dial: "+32" },
  { code: "BA", name: "Bosnia and Herzegovina", dial: "+387" },
  { code: "BG", name: "Bulgaria", dial: "+359" },
  { code: "HR", name: "Croatia", dial: "+385" },
  { code: "CZ", name: "Czechia", dial: "+420" },
  { code: "DK", name: "Denmark", dial: "+45" },
  { code: "EE", name: "Estonia", dial: "+372" },
  { code: "FI", name: "Finland", dial: "+358" },
  { code: "FR", name: "France", dial: "+33" },
  { code: "DE", name: "Germany", dial: "+49" },
  { code: "GR", name: "Greece", dial: "+30" },
  { code: "HU", name: "Hungary", dial: "+36" },
  { code: "IS", name: "Iceland", dial: "+354" },
  { code: "IE", name: "Ireland", dial: "+353" },
  { code: "IT", name: "Italy", dial: "+39" },
  { code: "XK", name: "Kosovo", dial: "+383" },
  { code: "LV", name: "Latvia", dial: "+371" },
  { code: "LI", name: "Liechtenstein", dial: "+423" },
  { code: "LT", name: "Lithuania", dial: "+370" },
  { code: "LU", name: "Luxembourg", dial: "+352" },
  { code: "MT", name: "Malta", dial: "+356" },
  { code: "MD", name: "Moldova", dial: "+373" },
  { code: "MC", name: "Monaco", dial: "+377" },
  { code: "ME", name: "Montenegro", dial: "+382" },
  { code: "NL", name: "Netherlands", dial: "+31" },
  { code: "MK", name: "North Macedonia", dial: "+389" },
  { code: "NO", name: "Norway", dial: "+47" },
  { code: "PL", name: "Poland", dial: "+48" },
  { code: "PT", name: "Portugal", dial: "+351" },
  { code: "RO", name: "Romania", dial: "+40" },
  { code: "RU", name: "Russia", dial: "+7" },
  { code: "SM", name: "San Marino", dial: "+378" },
  { code: "RS", name: "Serbia", dial: "+381" },
  { code: "SK", name: "Slovakia", dial: "+421" },
  { code: "SI", name: "Slovenia", dial: "+386" },
  { code: "ES", name: "Spain", dial: "+34" },
  { code: "SE", name: "Sweden", dial: "+46" },
  { code: "CH", name: "Switzerland", dial: "+41" },
  { code: "UA", name: "Ukraine", dial: "+380" },
  { code: "GB", name: "United Kingdom", dial: "+44" },
  { code: "VA", name: "Vatican City", dial: "+379" },

  // ── Oceania ──
  { code: "AU", name: "Australia", dial: "+61" },
  { code: "FJ", name: "Fiji", dial: "+679" },
  { code: "KI", name: "Kiribati", dial: "+686" },
  { code: "MH", name: "Marshall Islands", dial: "+692" },
  { code: "FM", name: "Micronesia", dial: "+691" },
  { code: "NR", name: "Nauru", dial: "+674" },
  { code: "NZ", name: "New Zealand", dial: "+64" },
  { code: "PW", name: "Palau", dial: "+680" },
  { code: "PG", name: "Papua New Guinea", dial: "+675" },
  { code: "WS", name: "Samoa", dial: "+685" },
  { code: "SB", name: "Solomon Islands", dial: "+677" },
  { code: "TO", name: "Tonga", dial: "+676" },
  { code: "TV", name: "Tuvalu", dial: "+688" },
  { code: "VU", name: "Vanuatu", dial: "+678" },
];

export const COUNTRY_CODES = RAW_COUNTRIES.map((c) => {
  const precise = PRECISE_RULES[c.code];
  const dialDigits = c.dial.replace("+", "").length;
  return {
    code: c.code,
    dial: c.dial,
    label: c.name,
    flag: isoToFlagEmoji(c.code),
    min: precise?.min ?? GENERIC_MIN_DIGITS,
    max: precise?.max ?? Math.max(GENERIC_MIN_DIGITS, 15 - dialDigits),
    leading: precise?.leading,
    leadingMessage: precise?.leadingMessage,
  };
}).sort((a, b) => a.label.localeCompare(b.label));
